import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2, UserCheck, HardHat, Plus, Package, QrCode, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TYPE_LABEL, PRIORITY_LABEL } from "@/lib/format";
import PhotoUploader from "@/components/PhotoUploader";

const TYPES = ["maintenance", "product_issue", "structural_damage", "new_installation", "miscellaneous"];
const PRIORITIES = ["urgent", "general", "routine"];

function filterOutletsByType(outlets, type) {
  const isInternal = type === "internal_job";
  return outlets.filter((o) => isInternal ? o.is_internal : !o.is_internal);
}

export default function TicketCreate() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedOutlet = searchParams.get("outlet") || "";
  const cameFromQr = !!preselectedOutlet;
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(preselectedOutlet);
  const [type, setType] = useState("maintenance");
  const [priority, setPriority] = useState("general");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fcs, setFcs] = useState([]);
  const [assignedFcId, setAssignedFcId] = useState("");

  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vName, setVName] = useState("");
  const [vTrade, setVTrade] = useState("");
  const [vPhone, setVPhone] = useState("");
  const [addingVendor, setAddingVendor] = useState(false);

  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [aName, setAName] = useState("");
  const [aCategory, setACategory] = useState("");
  const [aSerial, setASerial] = useState("");
  const [aNotes, setANotes] = useState("");
  const [addingAsset, setAddingAsset] = useState(false);

  const canManageVendors = user?.role === "admin" || user?.role === "manager" || user?.role === "op_manager";
  const canManageAssets = user?.role === "admin" || user?.role === "manager" || user?.role === "op_manager";

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/outlets");
      setOutlets(data);
    })();
    api.get("/vendors").then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/assets").then(({ data }) => setAssets(data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/users/facility-incharges").then(({ data }) => setFcs(data)).catch(() => {});
    }
  }, [user]);

  const canInternalJob = user?.role === "admin";
  const typeOptions = canInternalJob ? [...TYPES, "internal_job"] : TYPES;
  const outletChoices = filterOutletsByType(outlets, type);

  useEffect(() => {
    if (!outletChoices.length) { setOutletId(""); return; }
    if (!outletChoices.some(o => o.id === outletId)) {
      // Outlet Incharge and Outlet Staff are locked to their own outlet — never
      // let the dropdown default to a peer's outlet by accident.
      const restricted = user?.role === "outlet_incharge" || user?.role === "outlet_staff";
      if (restricted && user.outlet_ids?.length) {
        const own = outletChoices.find(o => user.outlet_ids.includes(o.id));
        setOutletId(own?.id || outletChoices[0].id);
      } else if (outletChoices.length === 1) {
        setOutletId(outletChoices[0].id);
      } else {
        setOutletId("");
      }
    }
    // eslint-disable-next-line
  }, [type, outlets]);

  const outletLocked = (user?.role === "outlet_incharge" || user?.role === "outlet_staff") && (user.outlet_ids?.length || 0) > 0;
  const restrictedNoOutlet = (user?.role === "outlet_incharge" || user?.role === "outlet_staff") && (user?.outlet_ids?.length || 0) === 0;

  const handleVendorChange = (val) => {
    if (val === "__add_new__") {
      setVName(""); setVTrade(""); setVPhone("");
      // Defer open past Radix Select's close cycle to avoid pointer-events clash
      setTimeout(() => setVendorDialogOpen(true), 60);
      return;
    }
    setVendorId(val === "__none__" ? "" : val);
  };

  const submitVendor = async (e) => {
    e.preventDefault();
    if (vName.trim().length < 2) return toast.error("Vendor name is too short");
    setAddingVendor(true);
    try {
      const { data } = await api.post("/vendors", {
        name: vName.trim(), trade: vTrade.trim(), phone: vPhone.trim(),
      });
      const next = [...vendors, data].sort((a, b) => a.name.localeCompare(b.name));
      setVendors(next);
      setVendorId(data.id);
      setVendorDialogOpen(false);
      toast.success(`${data.name} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add vendor");
    } finally { setAddingVendor(false); }
  };

  const handleAssetChange = (val) => {
    if (val === "__add_new__") {
      setAName(""); setACategory(""); setASerial(""); setANotes("");
      // Defer open past Radix Select's close cycle to avoid pointer-events clash
      setTimeout(() => setAssetDialogOpen(true), 60);
      return;
    }
    setAssetId(val === "__none__" ? "" : val);
  };

  const submitAsset = async (e) => {
    e.preventDefault();
    if (!outletId) return toast.error("Please select an outlet first");
    if (aName.trim().length < 2) return toast.error("Asset name is too short");
    setAddingAsset(true);
    try {
      const { data } = await api.post("/assets", {
        outlet_id: outletId,
        name: aName.trim(),
        category: aCategory.trim(),
        serial_number: aSerial.trim(),
        notes: aNotes.trim(),
      });
      const next = [...assets, data].sort((a, b) => a.name.localeCompare(b.name));
      setAssets(next);
      setAssetId(data.id);
      setAssetDialogOpen(false);
      toast.success(`${data.name} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add asset");
    } finally { setAddingAsset(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!outletId) return toast.error("Please select an outlet");
    if (title.trim().length < 3) return toast.error("Title is too short");
    setSaving(true);
    try {
      const payload = {
        outlet_id: outletId, type, priority,
        title: title.trim(), description: desc.trim(),
        photo_ids: photos,
      };
      if (type === "internal_job" && assignedFcId) payload.assigned_fc_id = assignedFcId;
      if (vendorId) payload.vendor_id = vendorId;
      if (assetId) payload.asset_id = assetId;
      const { data } = await api.post("/tickets", payload);
      toast.success("Ticket raised");
      nav(`/tickets/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create ticket");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <h1 className="font-display font-bold text-3xl text-slate-900">Raise a ticket</h1>
        <p className="text-sm text-slate-500 mt-1">Give as much detail and proof as you can — Facility Incharge picks it up from here.</p>
      </div>

      {restrictedNoOutlet && (
        <div data-testid="no-outlet-banner" className="rounded-xl bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">You have no outlet assigned yet.</div>
            <div className="text-[12px] mt-0.5">Ask your admin to open <span className="font-semibold">Manage → Users → Allot outlets</span> and pick the outlet you work at. You'll be able to raise tickets right after.</div>
          </div>
        </div>
      )}

      {cameFromQr && outletId && (() => {
        const o = outlets.find(x => x.id === outletId);
        if (!o) return null;
        return (
          <div data-testid="qr-outlet-banner" className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 text-sm flex items-center gap-2">
            <QrCode className="w-4 h-4 shrink-0" />
            <span>Scanned QR for <span className="font-semibold">{o.code} · {o.name}</span> — outlet pre-selected below.</span>
          </div>
        );
      })()}

      <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6 space-y-4">
        <div>
          <Label>Outlet</Label>
          <Select value={outletId} onValueChange={setOutletId} disabled={outletLocked}>
            <SelectTrigger data-testid="new-outlet" className="mt-1.5 h-11"><SelectValue placeholder={outletChoices.length ? "Choose outlet" : "No outlet available for this type"} /></SelectTrigger>
            <SelectContent>
              {outletChoices.map((o) => <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {outletLocked && (
            <div className="text-[11px] text-slate-500 mt-1">Locked to your assigned outlet — only an admin can move you.</div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="new-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {typeOptions.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="new-priority" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Title</Label>
          <Input data-testid="new-title" value={title} onChange={(e) => setTitle(e.target.value)}
            className="mt-1.5 h-11" placeholder="Short summary of the issue" required maxLength={140} />
        </div>

        <div>
          <Label className="flex items-center gap-1.5"><HardHat className="w-3.5 h-3.5 text-slate-500" /> Vendor / Contractor <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Select key={`v-${vendors.length}-${vendorId}`} value={vendorId || "__none__"} onValueChange={handleVendorChange}>
            <SelectTrigger data-testid="new-vendor" className="mt-1.5 h-11">
              <SelectValue placeholder="Pick vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No vendor yet</SelectItem>
              {vendors.length > 0 && <SelectSeparator />}
              {vendors.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name}{v.trade ? ` · ${v.trade}` : ""}
                </SelectItem>
              ))}
              {canManageVendors && (
                <>
                  <SelectSeparator />
                  <SelectItem value="__add_new__" data-testid="new-vendor-add-inline" className="text-indigo-700 font-semibold">
                    + Add new vendor…
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {outletId && type !== "internal_job" && (
          <div>
            <Label className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-slate-500" /> Asset <span className="text-slate-400 font-normal">(optional — helps track repeat breakdowns)</span></Label>
            {(() => {
              const outletAssets = assets.filter(a => a.outlet_id === outletId);
              return (
                <Select key={`a-${assets.length}-${assetId}-${outletId}`} value={assetId || "__none__"} onValueChange={handleAssetChange}>
                  <SelectTrigger data-testid="new-asset" className="mt-1.5 h-11">
                    <SelectValue placeholder={outletAssets.length ? "Pick asset" : "No assets for this outlet yet"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No specific asset</SelectItem>
                    {outletAssets.length > 0 && <SelectSeparator />}
                    {outletAssets.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}{a.category ? ` · ${a.category}` : ""}
                      </SelectItem>
                    ))}
                    {canManageAssets && (
                      <>
                        <SelectSeparator />
                        <SelectItem value="__add_new__" data-testid="new-asset-add-inline" className="text-indigo-700 font-semibold">
                          + Add new asset…
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              );
            })()}
          </div>
        )}

        {type === "internal_job" && user?.role === "admin" && (
          <div className="rounded-xl bg-indigo-50/60 border border-indigo-100 p-4">
            <Label className="flex items-center gap-1.5 text-indigo-900">
              <UserCheck className="w-4 h-4" /> Assign to Facility Incharge
            </Label>
            <Select value={assignedFcId} onValueChange={setAssignedFcId}>
              <SelectTrigger data-testid="new-assign-fc" className="mt-1.5 h-11 bg-white">
                <SelectValue placeholder={fcs.length ? "Pick who takes this internal job" : "No Facility Incharge available"} />
              </SelectTrigger>
              <SelectContent>
                {fcs.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-indigo-800/70 mt-2">The picked FC becomes the sole non-admin who can see this ticket.</p>
          </div>
        )}

        <div>
          <Label>Description</Label>
          <Textarea data-testid="new-description" value={desc} onChange={(e) => setDesc(e.target.value)}
            className="mt-1.5 min-h-32" placeholder="What's wrong? Any impact on operations?" />
        </div>

        <div>
          <Label>Photos (up to 4)</Label>
          <div className="mt-1.5">
            <PhotoUploader value={photos} onChange={setPhotos} max={4} testid="new-photos" />
          </div>
        </div>

        <div className="pt-2 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
          <Button type="button" variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="new-submit"
            className="h-11 px-5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Raise Ticket"}
          </Button>
        </div>
      </form>

      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> Quick-add vendor</DialogTitle></DialogHeader>
          <form onSubmit={submitVendor} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="quick-vendor-name" required autoFocus value={vName} onChange={(e) => setVName(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Ravi Electricals" />
            </div>
            <div>
              <Label>Trade / Specialty</Label>
              <Input data-testid="quick-vendor-trade" value={vTrade} onChange={(e) => setVTrade(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Electrician" />
            </div>
            <div>
              <Label>Phone number</Label>
              <Input data-testid="quick-vendor-phone" value={vPhone} onChange={(e) => setVPhone(e.target.value)} className="mt-1.5 h-11" placeholder="+91 98xxxxxxxx" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={addingVendor} data-testid="quick-vendor-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {addingVendor ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & select"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> Quick-add asset</DialogTitle></DialogHeader>
          <form onSubmit={submitAsset} className="space-y-3">
            <p className="text-[11px] text-slate-500">Adding to outlet: <span className="font-semibold text-slate-700">{outlets.find(o => o.id === outletId)?.name || "—"}</span></p>
            <div>
              <Label>Name</Label>
              <Input data-testid="quick-asset-name" required autoFocus value={aName} onChange={(e) => setAName(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. Blue Star split AC — counter" />
            </div>
            <div>
              <Label>Category</Label>
              <Input data-testid="quick-asset-category" list="quick-asset-category-list" value={aCategory} onChange={(e) => setACategory(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. HVAC, Refrigeration…" />
              <datalist id="quick-asset-category-list">
                <option value="HVAC" />
                <option value="Refrigeration" />
                <option value="Lighting" />
                <option value="Plumbing" />
                <option value="IT/Electronics" />
                <option value="Furniture" />
              </datalist>
            </div>
            <div>
              <Label>Serial / Model number</Label>
              <Input data-testid="quick-asset-serial" value={aSerial} onChange={(e) => setASerial(e.target.value)} className="mt-1.5 h-11" placeholder="Optional" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={addingAsset} data-testid="quick-asset-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {addingAsset ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & select"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
